import useDarkMode from 'contexts/ThemeProvider';

import defaultFrame from 'assets/images/switchback-frame.svg';
import reverseFrame from 'assets/images/switchback-reverse-frame.svg';

import color from 'atoms/colors/colors';

import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image, { ImageData } from 'quarks/Image';
import type { FlexProps } from 'quarks/interpolations/flex';
import { TextStyles } from 'quarks/styleProps/heading';

import ComponentButton from 'components/Button/ComponentButton';
import FeatureGrid from 'components/FeatureGrid/FeatureGrid';
import Form from 'components/Form';
import StatisticPanel from 'components/StatisticPanel/StatisticPanel';
import ComponentVideo from 'components/Video/Video';

// Scripts
import { formatHeadingSize } from 'utils/formatHeadingSize';
import { colorTheme, toCamelCase } from 'utils/functions';
import richTextParser from 'utils/richTextParser';

import type { ContentfulComponentSwitchback } from 'graphqlTypes';
import type { FC } from 'react';

interface SwitchbackProps extends FlexProps, ContentfulComponentSwitchback {
  backgroundIsDark: boolean;
}

type ReferenceComponentProps = SwitchbackProps;

export const ReferenceComponent: FC<ReferenceComponentProps> = ({ reference, backgroundIsDark, ...otherProps }) => {
  if (!reference) {
    return null;
  }
  switch (reference.__typename) {
    case 'ContentfulComponentImage':
      if (reference.imageDesktop || reference?.bynderImageDesktop) {
        return (
          <Image
            image={(reference?.bynderImageDesktop || reference.imageDesktop) as ImageData}
            alt={reference.altText || ''}
            boxShadow={reference?.boxShadow}
            maxHeight="auto"
            roundedCorners={reference?.roundedCorners}
            width="100%"
            {...otherProps}
            link={reference.link}
          />
        );
      }

      return null;
    case 'ContentfulComponentFeatureGrid':
      return <FeatureGrid backgroundIsDark={backgroundIsDark} isReferenced {...reference} />;

    case 'ContentfulComponentStatisticPanel':
      return <StatisticPanel backgroundIsDark={backgroundIsDark} isReferenced {...reference} />;

    case 'ContentfulComponentForm':
      return <Form backgroundIsDark={backgroundIsDark} {...reference} />;

    case 'ContentfulComponentVideo':
      return <ComponentVideo backgroundIsDark={backgroundIsDark} backgroundColor="None" {...reference} />;
  }

  return null;
};

const Switchback: FC<SwitchbackProps> = ({
  heading,
  headingColor,
  body,
  bodyColor,
  reference,
  featuredReference,
  reverse,
  callToAction,
  backgroundIsDark,
  imageFrame,
  ...props
}) => {
  const { headingSizeMobile, headingSizeTablet, headingSizeDesktop } = formatHeadingSize(props);
  const [isDark] = useDarkMode();

  return (
    <>
      <Flex
        flexDirection="column"
        gap={reverse ? '40px' : '96px'}
        justifyContent="space-between"
        alignItems="center"
        lg={{ flexDirection: 'row', gap: '40px' }}
        {...props}
      >
        <Flex flexDirection="column" gap="24px" width="100%" maxWidth="100%" lg={{ maxWidth: '570px' }}>
          {heading && (
            <Heading
              as="h2"
              textStyle={(headingSizeMobile as TextStyles) || 'h4'}
              fontWeight="bold"
              textColor={backgroundIsDark ? 'common-white' : colorTheme(toCamelCase(headingColor || 'Gray 900'))}
              md={{ textStyle: (headingSizeTablet as TextStyles) || 'h3', fontWeight: 'bold' }}
              lg={{ textStyle: (headingSizeDesktop as TextStyles) || 'h2', fontWeight: 'bold' }}
            >
              {heading}
            </Heading>
          )}
          {body && (
            <Flex
              flexDirection="column"
              gap="16px"
              textColor={
                backgroundIsDark
                  ? 'common-white'
                  : `${isDark ? colorTheme(bodyColor || 'Gray 700') : colorTheme(bodyColor || 'Gray 700')}`
              }
              css={`
                .MuiButton-root {
                  color: ${backgroundIsDark ? color.common.white : undefined};
                  align-self: flex-start;
                }
              `}
            >
              {body && richTextParser(body)}
            </Flex>
          )}
          {featuredReference && (
            <Flex
              width="100%"
              maxWidth="100%"
              marginTop={
                featuredReference?.__typename === 'ContentfulComponentForm' ||
                featuredReference?.__typename === 'ContentfulComponentFeatureGrid'
                  ? 0
                  : 16
              }
              paddingTop={
                featuredReference?.__typename === 'ContentfulComponentForm' ||
                featuredReference?.__typename === 'ContentfulComponentFeatureGrid'
                  ? 0
                  : 32
              }
              borderTop={
                featuredReference?.__typename === 'ContentfulComponentForm' ||
                featuredReference?.__typename === 'ContentfulComponentFeatureGrid'
                  ? 'unset'
                  : '2px solid'
              }
              borderColor={backgroundIsDark ? 'navy-900' : 'gray-200'}
            >
              {featuredReference && (
                <ReferenceComponent reference={featuredReference} backgroundIsDark={backgroundIsDark} />
              )}
            </Flex>
          )}
          {callToAction && (
            <Flex gap="16px" flexWrap="wrap">
              {callToAction.map(button => (
                <ComponentButton key={button?.label} {...button} />
              ))}
            </Flex>
          )}
        </Flex>
        <Flex
          position="relative"
          width="100%"
          maxWidth="100%"
          order={2}
          justifyContent="center"
          lg={{ maxWidth: '570px', order: reverse ? 1 : -1 }}
        >
          {reference && <ReferenceComponent reference={reference} backgroundIsDark={backgroundIsDark} />}
          {!imageFrame && (
            <Flex
              position="absolute"
              width="80%"
              top="50%"
              transform="translateY(-50%)"
              right={reverse ? '-10%' : undefined}
              left={reverse ? undefined : '-12%'}
              zIndex="-1"
              display="none"
              lg={{ display: backgroundIsDark ? 'none' : 'flex' }}
            >
              <Image
                image={{ file: { url: reverse ? defaultFrame : reverseFrame } }}
                alt="Switchback Reference Frame"
                width="100%"
                height="100%"
              />
            </Flex>
          )}
        </Flex>
      </Flex>
    </>
  );
};

export default Switchback;
